/**
 * Nueva campaña · Mensajes (`/activa/segmentaciones/:id/mensajes`, DISENO, Mensajes.dc.html; docs/activa/FASE-3-PLAN.md).
 * Del equipo. Un mensaje para cada grupo: si va, por qué canal, con qué oferta y qué texto.
 *
 * - No hace falta mandarle a todos: «Mandar sólo a este grupo» apaga los demás de un toque, y arriba de la lista dice
 *   cuántos van (la reunión con Llama Leads del 26/10: para mandarle WhatsApp sólo a Campeones había que apagarlos de a uno).
 * - A la izquierda, los grupos con su canal y su oferta, y el costo estimado total. Al tocar uno se abre su editor.
 * - La sugerencia del agente con el porqué, y «Volver a la sugerencia» si la cambiaste.
 * - Los textos salen de un catálogo por la temperatura del grupo y el tono de la marca: «Otra versión» pasa a la
 *   siguiente. Cambiar la oferta o el mínimo vuelve a armar el texto; «Recuperar mi texto» trae el anterior (un paso).
 * - «Escribir con IA» (Alan, 01/11/2026: la IA escribe y el operador aprueba), si el servidor la tiene prendida
 *   (`ia.disponible`): en un grupo por mail llena el asunto y el texto, que se siguen editando (lo anterior queda para
 *   «Recuperar mi texto»); en uno por el WhatsApp oficial propone una plantilla en su recuadro (`PropuestaWhatsApp`), que
 *   se edita y se guarda con el borrador. La vista previa le pasa los controles de Meta y de Mensajes en vivo. Nada sale
 *   ni va a Meta solo.
 * - La vista previa la arma el backend con la misma función que va a armar el envío, para una persona que de verdad lo
 *   recibe (sin bajas, fuera del control, con permiso). Con el cupón del grupo elegido, la previa muestra su código; sin
 *   elegir, «elegí el cupón».
 * - Los avisos son los del backend: los que no dejan enviar y los que sólo avisan.
 * - «Guardar borrador» en cualquier momento. Con los mensajes guardados y listos, «Preparar el envío» arma la campaña que
 *   WhatsApp oficial ofrece en su Audiencia (fase 4, `envios/components/PrepararEnvio.tsx`).
 * - Con cambios sin guardar, salir (el menú, «Atrás», los pasos, cerrar la pestaña) pregunta (`useAvisoSinGuardar`).
 * - Por el WhatsApp oficial no sale el texto (R3, 29/10/2026): en su lugar va «Plantilla de Meta», el selector del
 *   asistente con sus variables (`PlantillaDelGrupo`). «Preparar el envío» arma una campaña del oficial por plantilla.
 *   Los grupos por mail y los de WhatsApp sin la cuenta oficial siguen con su texto.
 * - «Crear las plantillas en Meta» (la parte C del 01/11/2026, `PlantillasDeLosGrupos`): con la propuesta de cada grupo de
 *   WhatsApp, crea y manda a aprobar sus plantillas, sólo cuando el operador lo confirma; cada grupo queda con su
 *   plantilla y el mapeo propuesto, que se confirma o se cambia en el grupo. Dice el estado de cada una en Meta.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, Check, Copy, Info, Loader2, RotateCcw, Shuffle, Sparkles, Undo2 } from "lucide-react";
import { VistaMail, VistaWhatsApp, type EstiloDeMarca } from "../../branding/components/Vistas";
import { num } from "../../campana-nueva/utils/format";
import { usePedido } from "../../emails/hooks/usePedido";
import { llamar } from "../../wa-oficial/components/base/comun";
import { ErrorTarjeta, Esqueleto } from "../../wa-oficial/components/estadisticas/comun";
import { Aviso as AvisoDePagina } from "../../../components/activa/Pagina";
import { textoError } from "../../wa-oficial/logic/respuesta";
import { Opciones } from "../../segmentaciones/components/Opciones";
import { Antetitulo, PasosCampana, btnSecundario } from "../../segmentaciones/components/PasosCampana";
import { pesos, rutaGrupos, rutaSegmentar } from "../../segmentaciones/logic/segmentar";
import { diaDe } from "../../descuentos/logic/descuentos";
import { PrepararEnvio } from "../../envios/components/PrepararEnvio";
import { useAvisoSinGuardar } from "../../../hooks/useAvisoSinGuardar";
import { mensajesApi } from "../api";
import { CuponDelGrupo } from "../components/CuponDelGrupo";
import { EscribirConIa } from "../components/EscribirConIa";
import { PropuestaWhatsApp } from "../components/PropuestaWhatsApp";
import { PlantillaDelGrupo } from "../components/PlantillaDelGrupo";
import { PlantillasDeLosGrupos } from "../components/PlantillasDeLosGrupos";
import { usePlantillasDeMeta, type PlantillasDeMeta } from "../hooks/usePlantillasDeMeta";
import { aplicarMail, aplicarPropuesta, cambiarCuerpo, cambiarEjemplo, descartarPropuesta } from "../logic/ia";
import { aplicarRespuesta, creadasDeLaVista, estadoDelGrupo, estadosConocidos, type Creadas, type EstadoConocido } from "../logic/plantillas";
import {
  CUPON,
  NOMBRE_CANAL,
  borradorInicial,
  cambiarCanal,
  cambiarMinimo,
  cambiarOferta,
  cambiarVa,
  cambioLaSugerencia,
  contextoDe,
  costoDelGrupo,
  cuponDeLaVista,
  elegirCupon,
  embellecerHtml,
  escribir,
  mapeoDeOtroGrupo,
  otraVersion,
  paraGuardar,
  partesConCupon,
  recuperarTexto,
  resumenDelGrupo,
  soloEste,
  textoCosto,
  textoCuantosVan,
  textoSugerencia,
  vaPorOficial,
  volverALaSugerencia,
  type Borrador,
  type Contexto,
} from "../logic/mensajes";
import type { Cupon } from "../../cupones/types";
import type { MapeoVariables } from "../../wa-oficial/types";
import type { Aviso, Canal, CuponDeLaVista, GrupoDeMensajes, Guardado, Previa, RespuestaDePlantillas, RespuestaIa, VistaDeMensajes } from "../types";

/** Los colores de los grupos, como en el reporte. */
const RAMPA = ["#2B2179", "#4735BE", "#695EDE", "#9A91EA", "#C3BDF3"];
const colorDe = (i: number) => RAMPA[Math.min(i, RAMPA.length - 1)];

const ESPERA_PREVIA_MS = 350;
/** Lo que se pregunta al salir con cambios sin guardar (el menú, «Atrás», los pasos). */
export const CONFIRMAR_SALIR = "Tenés cambios sin guardar en los mensajes. ¿Salir igual?";
const tarjeta = "rounded-2xl border border-slate-200 bg-white";

/** «Listos: 2 grupos por WhatsApp y 1 por mail (sale por Snov).»; todo por mail, «Listos: 2 grupos por mail (salen por Snov).» */
export function textoListos(porWhatsapp: number, porMail: number): string {
  const snov = `(${porMail === 1 ? "sale" : "salen"} por Snov)`;
  if (porWhatsapp === 0) return `Listos: ${porMail} ${porMail === 1 ? "grupo" : "grupos"} por mail ${snov}.`;
  const wa = `${porWhatsapp} ${porWhatsapp === 1 ? "grupo" : "grupos"} por WhatsApp`;
  return porMail > 0 ? `Listos: ${wa} y ${porMail} por mail ${snov}.` : `Listos: ${wa}.`;
}
const ayuda = "text-sm text-slate-600";
const btnChico =
  "min-h-[44px] px-3.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-800 hover:bg-slate-50 inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400";

const bloquean = (avisos: Aviso[] | undefined) => !!avisos?.some((a) => a.bloquea);

/** El cupón, en la vista previa: el código de verdad nace al enviar. */
function Cupon() {
  return <span className="rounded-md bg-violet-50 px-1.5 py-0.5 font-mono text-[13px] text-violet-800">elegí el cupón</span>;
}

const conCupon = (texto: string): ReactNode => partesConCupon(texto).map((p, i) => (p.cupon ? <Cupon key={i} /> : <span key={i}>{p.texto}</span>));

function ListaDeAvisos({ avisos, etiqueta = "Avisos del mensaje", testId = "avisos" }: { avisos: Aviso[]; etiqueta?: string; testId?: string }) {
  if (!avisos.length) return null;
  const orden = [...avisos.filter((a) => a.bloquea), ...avisos.filter((a) => !a.bloquea)];
  return (
    <ul className="space-y-2" data-testid={testId} aria-label={etiqueta}>
      {orden.map((a, i) => (
        <li
          key={`${a.codigo}-${i}`}
          className={`flex items-start gap-2.5 rounded-xl px-3.5 py-2.5 text-sm ${a.bloquea ? "border border-red-100 bg-red-50 text-red-800" : "bg-slate-50 text-slate-700"}`}
        >
          {a.bloquea ? <AlertTriangle size={16} className="mt-0.5 flex-none" aria-hidden="true" /> : <Info size={16} className="mt-0.5 flex-none text-violet-700" aria-hidden="true" />}
          <span>
            {a.bloquea && <span className="sr-only">No deja enviar: </span>}
            {a.texto}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** «Personalizar con»: suma la variable donde está el cursor del texto. */
function Variables({ variables, alElegir }: { variables: string[]; alElegir: (v: string) => void }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[13px] text-slate-600">Personalizar con:</span>
      {variables.map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => alElegir(v)}
          className="inline-flex min-h-[44px] items-center rounded-lg bg-slate-100 px-2.5 font-mono text-[13px] text-slate-700 hover:bg-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
        >
          {v}
        </button>
      ))}
    </div>
  );
}

function EditorDelGrupo({
  campanaId,
  vista,
  ctx,
  g,
  indice,
  b,
  avisosGuardados,
  cupones,
  elegidos,
  otrosVan,
  meta,
  deOtroGrupo,
  mapeoPropuesto,
  rechazo,
  alCambiar,
  alActualizar,
  alSoloEste,
  alPrevia,
  alCrearCupon,
}: {
  campanaId: string;
  vista: VistaDeMensajes;
  ctx: Contexto;
  g: GrupoDeMensajes;
  indice: number;
  b: Borrador;
  avisosGuardados: Aviso[];
  cupones: CuponDeLaVista[];
  elegidos: ReadonlyMap<string, string>;
  /** si algún otro grupo de la campaña va (para ofrecer «Mandar sólo a este grupo») */
  otrosVan: boolean;
  /** las plantillas de Meta de la marca (sólo con la cuenta del oficial) */
  meta: PlantillasDeMeta;
  /** el mapeo de otro grupo que ya usa esa plantilla */
  deOtroGrupo: (plantillaId: string) => MapeoVariables | null;
  /** la plantilla del grupo es la que se creó desde su propuesta: el mapeo y el «si falta» los propuso el sistema */
  mapeoPropuesto: boolean;
  /** Meta rechazó la plantilla que se creó desde la propuesta del grupo: el motivo; null si no */
  rechazo: string | null;
  alCambiar: (b: Borrador) => void;
  /** cambia el borrador del grupo como esté en ese momento (lo que vuelve de la IA llega después de unos segundos) */
  alActualizar: (f: (b: Borrador) => Borrador) => void;
  alSoloEste: () => void;
  alPrevia: (p: Previa | null) => void;
  alCrearCupon: (c: Cupon) => void;
}) {
  const [pestana, setPestana] = useState<"mensaje" | "html">("mensaje");
  const [previa, setPrevia] = useState<{ clave: string; datos: Previa } | null>(null);
  const [errorPrevia, setErrorPrevia] = useState("");
  const [reintento, setReintento] = useState(0);
  const [lindo, setLindo] = useState(false);
  const [copiado, setCopiado] = useState(false);
  // los avisos con los que volvió la propuesta de la IA, hasta que la vista previa la vuelva a mirar
  const [avisosIa, setAvisosIa] = useState<Aviso[] | null>(null);
  const textoRef = useRef<HTMLTextAreaElement>(null);
  const seq = useRef(0);

  const persona = g.ejemplo[b.canal];
  // por el WhatsApp oficial sale la plantilla, no el texto: los avisos de la previa son los de la plantilla
  const porOficial = vaPorOficial(b, vista);
  const pedido = {
    grupoId: g.id,
    canal: b.canal,
    oferta: b.oferta,
    minimo: b.oferta > 0 ? b.minimo : null,
    asunto: b.canal === "email" ? b.asunto : "",
    texto: b.texto,
    persona,
    cuponId: b.oferta > 0 ? b.cuponId : null,
    plantillaId: porOficial ? (b.plantillaId ?? null) : null,
    mapeo: porOficial && b.plantillaId ? (b.mapeo ?? {}) : null,
    // la propuesta de la IA, como está en la pantalla: la previa le pasa las reglas de Meta y las de Mensajes
    propuestaIa: porOficial ? (b.propuestaIa ?? null) : null,
  };
  const clave = JSON.stringify(pedido);
  const esMail = b.canal === "email";

  useEffect(() => {
    if (!b.va) return;
    const mio = ++seq.current;
    const t = setTimeout(async () => {
      const l = await llamar<Previa>(() => mensajesApi.previa(campanaId, pedido));
      if (mio !== seq.current) return;
      if (!l.ok) {
        setErrorPrevia(textoError(l));
        return;
      }
      setErrorPrevia("");
      setPrevia({ clave, datos: l.data });
      alPrevia(l.data);
    }, ESPERA_PREVIA_MS);
    return () => clearTimeout(t);
    // la previa depende sólo de lo que está en la pantalla (la clave)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave, b.va, reintento]);

  useEffect(() => {
    if (!esMail && pestana === "html") setPestana("mensaje");
  }, [esMail, pestana]);

  const sumarVariable = (v: string) => {
    const el = textoRef.current;
    const desde = el?.selectionStart ?? b.texto.length;
    const hasta = el?.selectionEnd ?? b.texto.length;
    alCambiar(escribir(b, "texto", b.texto.slice(0, desde) + v + b.texto.slice(hasta)));
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(desde + v.length, desde + v.length);
    });
  };

  const copiar = async (html: string) => {
    try {
      await navigator.clipboard.writeText(html);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      setCopiado(false);
    }
  };

  const estilo: EstiloDeMarca = { color: vista.marca.color, letra: vista.marca.letra, logo: vista.marca.logo, marca: vista.marca.marca };
  const reciben = g.reciben[b.canal];
  const avisos = previa?.datos.avisos ?? avisosGuardados;
  const sinWhatsApp = !vista.whatsapp.qr && !vista.whatsapp.oficial;
  // el código del cupón elegido: la caja de la vista previa lo muestra (el texto ya lo trae del servidor)
  const codigoElegido = b.oferta > 0 && b.cuponId ? (cupones.find((c) => c.id === b.cuponId)?.codigo ?? null) : null;
  const costo = costoDelGrupo(g, b);
  const html = previa?.datos.html ?? null;
  const actualizando = !!previa && previa.clave !== clave;
  // «Escribir con IA»: sólo con la IA prendida en el servidor, para el mail y para la plantilla del oficial
  const iaPrendida = !!vista.ia?.disponible;
  const alEscribir = (r: RespuestaIa) => {
    if (r.canal === "email") {
      alActualizar((x) => aplicarMail(x, r));
      return;
    }
    setAvisosIa(r.avisos);
    alActualizar((x) => aplicarPropuesta(x, r.plantilla));
  };
  const avisosPropuesta = (previa && previa.clave === clave ? previa.datos.propuesta?.avisos : undefined) ?? avisosIa ?? [];
  const revisandoPropuesta = !!b.propuestaIa && (!previa || previa.clave !== clave);

  return (
    <section className={`${tarjeta} min-w-0 flex-[999_1_560px] space-y-5 p-5 sm:p-6`} aria-labelledby="grupo-elegido">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-[1_1_240px] space-y-1">
          <h2 id="grupo-elegido" className="flex items-center gap-2.5 text-xl font-bold text-slate-900 sm:text-2xl">
            <span className="h-3.5 w-3.5 flex-none rounded-full" style={{ background: colorDe(indice) }} aria-hidden="true" />
            {g.nombre}
          </h2>
          {g.frase && <p className={ayuda}>{g.frase}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <label className="flex min-h-[44px] cursor-pointer items-center gap-2.5 text-[15px] font-semibold text-slate-900">
            <input type="checkbox" checked={b.va} onChange={(e) => alCambiar(cambiarVa(b, e.target.checked))} className="h-5 w-5 accent-violet-600" />
            Va en esta campaña
          </label>
          {otrosVan && (
            <button type="button" className={btnChico} onClick={alSoloEste}>
              Mandar sólo a este grupo
            </button>
          )}
        </div>
      </div>

      {!b.va && (
        <div className="flex flex-col items-start gap-3 rounded-xl bg-slate-50 p-6">
          <p className="text-base text-slate-800">Este grupo no recibe nada en esta campaña.</p>
          <button type="button" className={btnSecundario} onClick={() => alCambiar(cambiarVa(b, true))}>
            Incluirlo
          </button>
        </div>
      )}

      {b.va && (
        <>
          {g.sugerencia && (
            <div className="flex flex-wrap items-start gap-3 rounded-xl bg-violet-50 px-4 py-3.5" data-testid="sugerencia">
              <Sparkles size={18} className="mt-0.5 flex-none text-violet-700" aria-hidden="true" />
              <div className="min-w-0 flex-[1_1_260px] space-y-1">
                <p className="font-bold text-violet-950">El agente sugiere: {textoSugerencia(g.sugerencia)}</p>
                <p className="text-[15px] text-slate-700">{g.sugerencia.porque.oferta}</p>
                <p className="text-[15px] text-slate-700">{g.sugerencia.porque.canal}</p>
              </div>
              {cambioLaSugerencia(g, ctx, b) && (
                <button type="button" className={btnChico} onClick={() => alCambiar(volverALaSugerencia(g, ctx, b))}>
                  <RotateCcw size={15} aria-hidden="true" />
                  Volver a la sugerencia
                </button>
              )}
            </div>
          )}

          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Opciones<Canal>
                nombre={`canal-${g.id}`}
                leyenda="Canal"
                opciones={["email", "whatsapp"]}
                valor={b.canal}
                onCambio={(c) => alCambiar(cambiarCanal(b, c))}
                etiqueta={(c) => `${NOMBRE_CANAL[c]} · ${num(g.reciben[c])}`}
              />
              {b.canal === "whatsapp" && sinWhatsApp && <p className="text-sm text-amber-800">La marca todavía no tiene WhatsApp conectado: conectá una línea o la cuenta oficial antes de enviar.</p>}
            </div>
            <div className="space-y-2">
              <Opciones<number>
                nombre={`oferta-${g.id}`}
                leyenda="Oferta"
                // una oferta guardada que Descuentos ya no deja se sigue viendo elegida (con su aviso)
                opciones={[...new Set([...vista.descuentos.ofertas, b.oferta])].sort((x, y) => x - y)}
                valor={b.oferta}
                onCambio={(o) => alCambiar(cambiarOferta(g, ctx, b, o))}
                etiqueta={(o) => (o === 0 ? "Sin descuento" : `${o}%`)}
              />
            </div>
          </div>

          {b.oferta > 0 && (
            <div className="grid items-end gap-5 sm:grid-cols-3">
              <label className="block space-y-2">
                <span className="block text-sm font-semibold text-slate-900">Compra mínima</span>
                <select
                  value={b.minimo === null ? "" : String(b.minimo)}
                  onChange={(e) => alCambiar(cambiarMinimo(g, ctx, b, e.target.value ? Number(e.target.value) : null))}
                  className="min-h-[44px] w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
                >
                  <option value="">Sin mínimo</option>
                  {vista.descuentos.compraMinima !== null && <option value={String(vista.descuentos.compraMinima)}>{pesos(vista.descuentos.compraMinima)}</option>}
                  {b.minimo !== null && b.minimo !== vista.descuentos.compraMinima && <option value={String(b.minimo)}>{pesos(b.minimo)}</option>}
                </select>
              </label>
              <CuponDelGrupo
                grupoId={g.id}
                b={b}
                cupones={cupones}
                elegidos={elegidos}
                vigenciaDias={vista.vigenciaDias}
                alElegir={(id) => alCambiar(elegirCupon(b, id))}
                alCrear={(c) => {
                  alCrearCupon(c);
                  alCambiar(elegirCupon(b, c.id));
                }}
              />
            </div>
          )}

          {porOficial ? (
            <section className="space-y-4" aria-labelledby={`plantilla-${g.id}`}>
              <div className="space-y-1">
                <h3 id={`plantilla-${g.id}`} className="text-base font-semibold text-slate-900">
                  Plantilla de Meta
                </h3>
                <p className={ayuda}>
                  Por WhatsApp oficial no sale un texto: sale una plantilla aprobada por Meta. Los grupos con la misma plantilla y las mismas variables salen en una misma campaña.
                </p>
              </div>
              {rechazo !== null && (
                <p className="flex items-start gap-2 rounded-xl border border-red-100 bg-red-50 px-3.5 py-2.5 text-sm text-red-800" data-testid="plantilla-rechazada">
                  <AlertTriangle size={16} className="mt-0.5 flex-none" aria-hidden="true" />
                  <span>
                    Meta rechazó la plantilla de esta propuesta{rechazo ? `: ${rechazo}` : ""}. {iaPrendida ? "Escribí otra con IA o corregí" : "Corregí"} la propuesta, guardá y creala de nuevo.
                  </span>
                </p>
              )}
              {iaPrendida && <EscribirConIa campanaId={campanaId} grupoId={g.id} b={b} alEscribir={alEscribir} />}
              {b.propuestaIa && (
                <PropuestaWhatsApp
                  grupoId={g.id}
                  p={b.propuestaIa}
                  marca={vista.marca.marca}
                  avisos={<ListaDeAvisos avisos={avisosPropuesta} etiqueta="Avisos de la propuesta" testId="avisos-propuesta" />}
                  revisando={revisandoPropuesta}
                  alCambiarCuerpo={(c) => alCambiar(cambiarCuerpo(b, c))}
                  alCambiarEjemplo={(n, e) => alCambiar(cambiarEjemplo(b, n, e))}
                  alDescartar={() => {
                    setAvisosIa(null);
                    alCambiar(descartarPropuesta(b));
                  }}
                />
              )}
              <PlantillaDelGrupo
                b={b}
                guardadaId={g.guardado?.plantillaId ?? ""}
                meta={meta}
                campos={vista.campos ?? []}
                deOtroGrupo={deOtroGrupo}
                mapeoPropuesto={mapeoPropuesto}
                alCambiar={alCambiar}
              />
              {errorPrevia && (
                <div className="space-y-2">
                  <ErrorTarjeta texto={errorPrevia} />
                  <button type="button" className={btnChico} onClick={() => setReintento((n) => n + 1)}>
                    Probar de nuevo
                  </button>
                </div>
              )}
              <ListaDeAvisos avisos={avisos} />
            </section>
          ) : (
            <div className="space-y-4">
              {esMail && iaPrendida && <EscribirConIa campanaId={campanaId} grupoId={g.id} b={b} alEscribir={alEscribir} />}
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100">
                <div role="tablist" aria-label="Mensaje" className="flex gap-5">
                  {(["mensaje", ...(esMail ? ["html"] : [])] as ("mensaje" | "html")[]).map((p) => (
                    <button
                      key={p}
                      type="button"
                      role="tab"
                      aria-selected={pestana === p}
                      onClick={() => setPestana(p)}
                      className={`-mb-px inline-flex min-h-[44px] items-center border-b-2 px-1 text-[15px] font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 ${
                        pestana === p ? "border-violet-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
                      }`}
                    >
                      {p === "mensaje" ? "Mensaje" : "HTML"}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 pb-2">
                  {pestana === "html" && html && (
                    <>
                      <button type="button" className={btnChico} aria-pressed={lindo} onClick={() => setLindo(!lindo)}>
                        {lindo ? "Ver como sale" : "Embellecer"}
                      </button>
                      <button type="button" className={btnChico} onClick={() => void copiar(lindo ? embellecerHtml(html) : html)}>
                        {copiado ? <Check size={15} aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}
                        {copiado ? "Copiado" : "Copiar"}
                      </button>
                    </>
                  )}
                  {b.respaldo && (
                    <button type="button" className={btnChico} onClick={() => alCambiar(recuperarTexto(b))}>
                      <Undo2 size={15} aria-hidden="true" />
                      Recuperar mi texto
                    </button>
                  )}
                  <button type="button" className={btnChico} onClick={() => alCambiar(otraVersion(g, ctx, b))}>
                    <Shuffle size={15} aria-hidden="true" />
                    Otra versión
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap items-start gap-5">
                <div className="min-w-0 flex-[1_1_280px] space-y-3.5">
                  {pestana === "mensaje" ? (
                    <>
                      {esMail && (
                        <label className="block space-y-2">
                          <span className="block text-sm font-semibold text-slate-900">Asunto</span>
                          <input
                            value={b.asunto}
                            maxLength={150}
                            onChange={(e) => alCambiar(escribir(b, "asunto", e.target.value))}
                            className="min-h-[44px] w-full rounded-xl border border-slate-200 bg-white px-3 text-[15px] text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
                          />
                        </label>
                      )}
                      <label className="block space-y-2">
                        <span className="block text-sm font-semibold text-slate-900">Texto</span>
                        <textarea
                          ref={textoRef}
                          value={b.texto}
                          rows={7}
                          maxLength={1500}
                          onChange={(e) => alCambiar(escribir(b, "texto", e.target.value))}
                          className="w-full resize-y rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-[15px] leading-normal text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
                        />
                      </label>
                      <Variables variables={vista.variables} alElegir={sumarVariable} />
                    </>
                  ) : html ? (
                    <pre className="max-h-[480px] overflow-auto whitespace-pre-wrap break-all rounded-xl bg-slate-900 p-4 font-mono text-[13px] leading-relaxed text-slate-100" data-testid="html">
                      {lindo ? embellecerHtml(html) : html}
                    </pre>
                  ) : (
                    <Esqueleto alto="h-48" etiqueta="Armando el HTML" />
                  )}
                  <ListaDeAvisos avisos={avisos} />
                </div>

                <div className="min-w-0 flex-[1_1_280px] space-y-2.5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <span aria-hidden="true">● </span>
                    {persona?.nombre ? `Así lo ve ${persona.nombre}` : persona ? "Así lo ve una persona del grupo" : "Así lo vería una persona del grupo"}
                    {actualizando && <span className="ml-2 normal-case tracking-normal text-slate-400">actualizando…</span>}
                  </p>
                  {errorPrevia && (
                    <div className="space-y-2">
                      <ErrorTarjeta texto={errorPrevia} />
                      <button type="button" className={btnChico} onClick={() => setReintento((n) => n + 1)}>
                        Probar de nuevo
                      </button>
                    </div>
                  )}
                  {!previa && !errorPrevia && <Esqueleto alto="h-64" etiqueta="Armando la vista previa" />}
                  {previa &&
                    (esMail ? (
                      <VistaMail
                        estilo={estilo}
                        asunto={previa.datos.asunto.split(CUPON).join("código")}
                        cuerpo={conCupon(previa.datos.texto)}
                        codigo={b.oferta > 0 ? (codigoElegido ?? <Cupon />) : null}
                        firma={previa.datos.firma}
                        boton={vista.marca.web ? "Pedir ahora" : null}
                        pie={previa.datos.pie}
                      />
                    ) : (
                      <VistaWhatsApp estilo={estilo} cuerpo={conCupon(previa.datos.texto)} codigo={null} firma={previa.datos.firma} pie={previa.datos.pie} />
                    ))}
                </div>
              </div>
            </div>
          )}

          {reciben === 0 && (
            <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="nadie-recibe">
              Nadie de este grupo lo recibe por {NOMBRE_CANAL[b.canal]}: {b.canal === "email" ? "no tienen mail" : "no tienen celular"} o no dieron permiso.
            </p>
          )}
          <div className="flex flex-wrap justify-between gap-3 rounded-xl bg-slate-50 px-4 py-3.5 text-[15px] text-slate-800">
            <span>
              Reciben <strong>{num(reciben)}</strong> por {NOMBRE_CANAL[b.canal]}
            </span>
            <span>
              Costo estimado del descuento: <strong>{textoCosto(costo, g.uso.observado)}</strong>
            </span>
          </div>
          {g.uso.observado && b.oferta > 0 && (
            <p className="text-[13px] text-slate-500" data-testid={`uso-observado-${g.id}`}>
              Con el uso real del cupón en las campañas anteriores de este grupo: lo usó el {num(Math.round(g.uso.valor * 100))}% de los que compraron.
            </p>
          )}
        </>
      )}
    </section>
  );
}

function PantallaMensajes({ vista: inicial }: { vista: VistaDeMensajes }) {
  const { id = "" } = useParams();
  const ctx = contextoDe(inicial);
  const [borradores, setBorradores] = useState<Record<string, Borrador>>(() => Object.fromEntries(inicial.grupos.map((g) => [g.id, borradorInicial(g, ctx)])));
  // el catálogo de Cupones (fase 4): suma los que se crean desde acá
  const [cupones, setCupones] = useState<CuponDeLaVista[]>(() => inicial.cupones ?? []);
  // las plantillas de Meta, para los grupos que van por el WhatsApp oficial (R3): sin la cuenta no se piden
  const meta = usePlantillasDeMeta(!!inicial.whatsapp.oficial);
  // cuponId → grupo, de lo elegido en la pantalla: un cupón no lo pueden llevar dos grupos
  const elegidos = new Map(
    inicial.grupos.flatMap((x) => {
      const bx = borradores[x.id];
      return bx?.oferta > 0 && bx.cuponId ? [[bx.cuponId, x.id] as [string, string]] : [];
    }),
  );
  const [sel, setSel] = useState(inicial.grupos[0]?.id ?? "");
  const [servidor, setServidor] = useState<{ avisos: Record<string, Aviso[]>; listo: boolean; actualizado: VistaDeMensajes["actualizado"]; guardados: number }>(() => ({
    avisos: Object.fromEntries(inicial.grupos.map((g) => [g.id, g.avisos])),
    listo: inicial.listo,
    actualizado: inicial.actualizado,
    guardados: inicial.grupos.filter((g) => g.guardado).length,
  }));
  const [ultimasPrevias, setUltimasPrevias] = useState<Record<string, Aviso[]>>({});
  // «Crear las plantillas en Meta»: qué plantilla salió de la propuesta de cada grupo, y su estado hasta que llegue la lista
  const [creadas, setCreadas] = useState<Creadas>(() => creadasDeLaVista(inicial));
  const [estadosDeCrear, setEstadosDeCrear] = useState<Record<string, EstadoConocido>>({});
  const conocido = estadosConocidos(meta.plantillas, estadosDeCrear);
  const [sinGuardar, setSinGuardar] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  useAvisoSinGuardar(sinGuardar, CONFIRMAR_SALIR);

  const cambiar = (grupoId: string, b: Borrador) => {
    setBorradores((x) => ({ ...x, [grupoId]: b }));
    setSinGuardar(true);
  };

  const delServidor = (d: Guardado) =>
    setServidor({
      avisos: Object.fromEntries(d.grupos.map((g) => [g.id, g.avisos])),
      listo: d.listo,
      actualizado: d.actualizado,
      guardados: d.grupos.filter((g) => g.guardado).length,
    });

  /** Guarda el borrador de todos los grupos; false si no se pudo (el error queda arriba). */
  const guardar = async (): Promise<boolean> => {
    setGuardando(true);
    setError("");
    const l = await llamar<Guardado>(() => mensajesApi.guardar(id, inicial.grupos.map((g) => paraGuardar(g.id, borradores[g.id]))));
    setGuardando(false);
    if (!l.ok) {
      setError(textoError(l));
      const campo = typeof l.extra.campo === "string" ? /^grupos\.([^.]+)\./.exec(l.extra.campo) : null;
      if (campo && inicial.grupos.some((g) => g.id === campo[1])) setSel(campo[1]);
      return false;
    }
    delServidor(l.data);
    setSinGuardar(false);
    return true;
  };

  /** Lo que dejó «Crear las plantillas en Meta»: la plantilla y el mapeo de cada grupo ya quedaron guardados. */
  const alCrearPlantillas = (r: RespuestaDePlantillas) => {
    const x = aplicarRespuesta(borradores, creadas, r);
    setBorradores(x.borradores);
    setCreadas(x.creadas);
    setEstadosDeCrear((e) => ({ ...e, ...x.estados }));
    delServidor(r.mensajes);
    // los avisos de esos grupos son los del servidor hasta que la vista previa los vuelva a mirar
    const cambiados = new Set(r.grupos.filter((g) => g.asignada).map((g) => g.grupoId));
    setUltimasPrevias((p) => Object.fromEntries(Object.entries(p).filter(([grupoId]) => !cambiados.has(grupoId))));
    void meta.recargar();
  };

  /** El motivo con el que Meta rechazó la plantilla de la propuesta del grupo; null si no la rechazó. */
  const rechazoDe = (grupoId: string) => {
    const e = estadoDelGrupo(borradores[grupoId], creadas[grupoId], conocido);
    return e.tipo === "rechazada" ? e.motivo : null;
  };

  const total = inicial.grupos.reduce((s, g) => s + costoDelGrupo(g, borradores[g.id]), 0);
  const observado = inicial.grupos.every((g) => g.uso.observado);
  const g = inicial.grupos.find((x) => x.id === sel) ?? inicial.grupos[0];
  const indice = inicial.grupos.findIndex((x) => x.id === g?.id);
  const avisosDe = (grupoId: string) => ultimasPrevias[grupoId] ?? servidor.avisos[grupoId] ?? [];
  const estadoGuardado = sinGuardar ? "Cambios sin guardar" : servidor.actualizado ? `Borrador guardado el ${diaDe(servidor.actualizado.at)}` : "Sin guardar todavía";
  const sinWhatsAppEnLaMarca = !inicial.whatsapp.qr && !inicial.whatsapp.oficial;
  const faltanAntes = [!inicial.descuentos.guardado, !inicial.marca.guardado, sinWhatsAppEnLaMarca].filter(Boolean).length;
  // qué sale por cada canal: el pie lo dice antes de tocar «Preparar el envío» (sólo WhatsApp se arma acá)
  const van = inicial.grupos.filter((x) => borradores[x.id]?.va);
  const porWhatsapp = van.filter((x) => borradores[x.id].canal === "whatsapp").length;
  const porMail = van.length - porWhatsapp;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-[1_1_220px]">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Nueva campaña</p>
          <p className="truncate text-lg font-semibold text-slate-900">{inicial.campana.nombre}</p>
        </div>
        <span className="text-sm text-slate-600" role="status">
          {estadoGuardado}
        </span>
        <button type="button" className={btnSecundario} onClick={() => void guardar()} disabled={guardando}>
          {guardando && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
          Guardar borrador
        </button>
      </div>
      <PasosCampana actual={2} rutas={[rutaSegmentar(id), rutaGrupos(id), null]} />

      <div className="space-y-1.5">
        <Antetitulo>Paso 3 de 4</Antetitulo>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Un mensaje para cada grupo que va</h1>
        <p className="text-sm sm:text-base text-slate-600">
          El agente propone canal, oferta y texto. Cambiá lo que quieras. No hace falta mandarle a todos: en cada grupo elegís si va, o tocás «Mandar sólo a este grupo».
        </p>
      </div>

      {faltanAntes > 0 && (
        <AvisoDePagina tono="atencion" titulo={`Antes de enviar ${faltanAntes === 1 ? "te falta 1 cosa" : `te faltan ${faltanAntes} cosas`}`} testId="antes-de-enviar">
          <ul className="mt-1 list-disc space-y-1 pl-5">
            {!inicial.descuentos.guardado && (
              <li data-testid="sin-descuentos">
                Todavía no guardaste los descuentos de esta marca: los grupos van sin descuento.{" "}
                <Link to="/activa/descuentos" className="font-semibold underline">
                  Cargar descuentos
                </Link>
              </li>
            )}
            {!inicial.marca.guardado && (
              <li data-testid="sin-branding">
                La vista previa usa los colores de siempre: cargá tu logo, tu color y tu web.{" "}
                <Link to="/activa/branding" className="font-semibold underline">
                  Cargar tu marca
                </Link>
              </li>
            )}
            {sinWhatsAppEnLaMarca && (
              <li data-testid="sin-whatsapp">
                La marca no tiene WhatsApp conectado: el agente pone todos los grupos por mail.{" "}
                <Link to="/activa/oficial/configuracion" className="font-semibold underline">
                  Conectar WhatsApp
                </Link>
              </li>
            )}
          </ul>
        </AvisoDePagina>
      )}
      {error && <ErrorTarjeta texto={error} />}

      <div className="flex flex-wrap items-start gap-5">
        <aside aria-label="Grupos" className="flex w-full max-w-full flex-[1_1_260px] flex-col gap-2 lg:w-auto">
          <p className="px-1 text-sm font-semibold text-slate-700" data-testid="cuantos-van">
            {textoCuantosVan(van.length, inicial.grupos.length)}
          </p>
          <div className={`${tarjeta} flex gap-1 overflow-x-auto p-2 sm:flex-col sm:overflow-visible`}>
            {inicial.grupos.map((x, i) => {
              const b = borradores[x.id];
              const elegido = x.id === g?.id;
              const revisar = b.va && bloquean(avisosDe(x.id));
              return (
                <button
                  key={x.id}
                  type="button"
                  aria-pressed={elegido}
                  onClick={() => setSel(x.id)}
                  className={`flex min-h-[44px] w-[220px] flex-none items-center gap-3 rounded-xl border p-3.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 sm:w-full ${
                    elegido ? "border-violet-600 bg-white ring-1 ring-violet-600" : "border-transparent hover:bg-slate-50"
                  }`}
                >
                  <span className="h-3 w-3 flex-none rounded-full" style={{ background: colorDe(i), opacity: b.va ? 1 : 0.5 }} aria-hidden="true" />
                  <span className={`min-w-0 flex-1 ${b.va ? "" : "opacity-60"}`}>
                    <span className="block text-[15px] font-semibold text-slate-900">{x.nombre}</span>
                    <span className="block text-[13px] text-slate-600">{resumenDelGrupo(b)}</span>
                  </span>
                  {revisar && <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700">Revisar</span>}
                </button>
              );
            })}
          </div>
          <div className={`${tarjeta} space-y-1 p-4`}>
            <p className="text-sm text-slate-600">Costo estimado de los descuentos</p>
            <p className="text-2xl font-bold text-slate-900" data-testid="costo-total">
              {textoCosto(total, observado)}
            </p>
            <p className="text-[13px] text-slate-600">
              {inicial.descuentos.maximo !== null ? `Tu máximo: ${inicial.descuentos.maximo}% · lo cambiás en ` : "Sin máximo cargado · cargalo en "}
              <Link to="/activa/descuentos" className="font-semibold text-violet-700 underline">
                Descuentos
              </Link>
            </p>
            {!observado && (
              <p className="text-[13px] text-slate-500" data-testid="costo-tope">
                {inicial.grupos.some((x) => x.uso.observado)
                  ? "Es un tope para los grupos sin campañas medidas: se toma que todos usan el cupón."
                  : "Es un tope: todavía no hay campañas medidas para saber cuántos usan el cupón."}
              </p>
            )}
          </div>
        </aside>

        {g && (
          <EditorDelGrupo
            key={g.id}
            campanaId={id}
            vista={inicial}
            ctx={ctx}
            g={g}
            indice={indice}
            b={borradores[g.id]}
            avisosGuardados={servidor.avisos[g.id] ?? []}
            cupones={cupones}
            elegidos={elegidos}
            otrosVan={van.some((x) => x.id !== g.id)}
            meta={meta}
            deOtroGrupo={(plantillaId) => mapeoDeOtroGrupo(borradores, g.id, plantillaId)}
            mapeoPropuesto={!!borradores[g.id].plantillaId && creadas[g.id]?.plantillaId === borradores[g.id].plantillaId}
            rechazo={rechazoDe(g.id)}
            alCambiar={(b) => cambiar(g.id, b)}
            alActualizar={(f) => {
              const grupoId = g.id;
              setBorradores((x) => ({ ...x, [grupoId]: f(x[grupoId]) }));
              setSinGuardar(true);
            }}
            alSoloEste={() => {
              setBorradores((x) => soloEste(x, g.id));
              setSinGuardar(true);
            }}
            alPrevia={(p) => setUltimasPrevias((x) => ({ ...x, [g.id]: p?.avisos ?? [] }))}
            alCrearCupon={(c) => setCupones((x) => [cuponDeLaVista(c), ...x])}
          />
        )}
      </div>

      <PlantillasDeLosGrupos
        campanaId={id}
        vista={inicial}
        borradores={borradores}
        creadas={creadas}
        conocido={conocido}
        cupo={meta.cupo}
        sinGuardar={sinGuardar}
        guardar={guardar}
        alCrear={alCrearPlantillas}
        alElegirGrupo={(grupoId) => {
          setSel(grupoId);
          document.getElementById("grupo-elegido")?.scrollIntoView?.({ behavior: "smooth", block: "start" });
        }}
        alConsultar={meta.recargar}
      />

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4">
        <Link to={rutaGrupos(id)} className={btnSecundario}>
          Atrás
        </Link>
        {!sinGuardar && servidor.guardados > 0 && servidor.listo && van.length > 0 && (
          <p className="text-sm text-emerald-700" data-testid="listo">
            {textoListos(porWhatsapp, porMail)}
          </p>
        )}
        <PrepararEnvio segmentacionId={id} listo={servidor.listo} sinGuardar={sinGuardar} porWhatsapp={porWhatsapp} />
      </div>
    </div>
  );
}

export default function MensajesPage() {
  const { id = "" } = useParams();
  const pedido = usePedido<VistaDeMensajes>(() => mensajesApi.ver(id), `mensajes:${id}`);
  const v = pedido.dato;
  if (v) return <PantallaMensajes key={v.campana.id} vista={v} />;
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
      <p className="text-sm font-semibold text-slate-900">Nueva campaña</p>
      <PasosCampana actual={2} rutas={[rutaSegmentar(id), rutaGrupos(id), null]} />
      {pedido.error && (
        <div className="space-y-3">
          <ErrorTarjeta texto={pedido.error} onReintentar={pedido.code === "NOMBRES_PENDIENTES" || pedido.code === "ESTADO_INVALIDO" ? undefined : pedido.recargar} />
          {(pedido.code === "NOMBRES_PENDIENTES" || pedido.code === "ESTADO_INVALIDO") && (
            <Link to={rutaGrupos(id)} className={btnSecundario}>
              Volver a Grupos
            </Link>
          )}
        </div>
      )}
      {pedido.cargando && !pedido.error && <Esqueleto alto="h-96" etiqueta="Cargando los mensajes" />}
    </div>
  );
}
